import React from 'react';
import { Sidebar } from 'primereact/sidebar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import './HelpSidebar.css';

interface HelpSidebarProps {
  visible: boolean;
  onHide: () => void;
  isAdmin: boolean;
  isUserAdmin?: boolean;
}

export const HelpSidebar: React.FC<HelpSidebarProps> = ({ visible, onHide, isAdmin, isUserAdmin = false }) => {
  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      className="help-sidebar"
      style={{ width: '600px' }}
    >
      <div className="help-content">
        <h2>📖 Garage Support Tool - User Manual</h2>
        
        <TabView>
          <TabPanel header="Getting Started">
            <div className="help-section">
              <h3>Welcome to Garage Support Tool</h3>
              <p>
                The Garage Support Tool (GST) helps you manage customer data for automotive workshops.
                This manual will guide you through all features available to you.
              </p>

              <h4>🔐 Your Access Level</h4>
              <div className={`access-badge ${isAdmin ? 'admin' : 'user'}`}>
                {isAdmin ? '👑 Administrator' : '👤 Regular User'}
              </div>
              
              {isAdmin ? (
                <p>
                  As an <strong>Administrator</strong>, you have full access to all organizations and customers.
                  You can view, create, edit, and delete customers across all organizations.
                </p>
              ) : (
                <p>
                  As a <strong>Regular User</strong>, you can manage customers within your assigned organization.
                  You can view, create, edit, and delete customers from your organization only.
                </p>
              )}

              <h4>📋 Main Features</h4>
              <ul>
                <li>View and search customer data</li>
                <li>Create new customers</li>
                <li>Edit customer information inline or via form</li>
                <li>Delete customers (single or multiple)</li>
                <li>Import customers from CSV files</li>
                <li>Export customers to CSV files</li>
                <li>Filter by organization and country</li>
                <li>Data validation with visual indicators</li>
                {isAdmin && <li>View change history (audit log)</li>}
                {isUserAdmin && <li>Manage user accounts (create, edit, delete)</li>}
              </ul>
            </div>
          </TabPanel>

          <TabPanel header="Customer Management">
            <div className="help-section">
              <Accordion multiple>
                <AccordionTab header="📝 Creating Customers">
                  <h4>How to Create a New Customer</h4>
                  <ol>
                    <li>Click the <strong>+ New</strong> button in the toolbar</li>
                    <li>Fill in the customer form:
                      <ul>
                        <li><strong>Customer Number</strong> - Unique identifier (required)</li>
                        <li><strong>Primary Email</strong> - Main contact email (required)</li>
                        <li><strong>Secondary Email</strong> - Additional email (optional)</li>
                        <li><strong>First Name</strong> - Customer's first name (optional)</li>
                        <li><strong>Surname</strong> - Customer's last name (required)</li>
                        <li><strong>Company</strong> - Company name (required)</li>
                        <li><strong>Company 2</strong> - Additional company info (optional)</li>
                        <li><strong>Country</strong> - Select from dropdown (required)</li>
                        <li><strong>Language</strong> - Select from dropdown (required)</li>
                        <li><strong>Telephone</strong> - Contact number (optional)</li>
                        {isAdmin && <li><strong>Organization</strong> - Select organization (required for admins)</li>}
                      </ul>
                    </li>
                    <li>Check data access permissions:
                      <ul>
                        <li>🚗 <strong>PKW Data</strong> - Passenger car data access</li>
                        <li>🚚 <strong>NKW Data</strong> - Commercial vehicle data access</li>
                      </ul>
                    </li>
                    <li>Click <strong>Save</strong> to create the customer</li>
                  </ol>
                  
                  {!isAdmin && (
                    <div className="info-box">
                      <strong>Note:</strong> New customers are automatically assigned to your organization.
                    </div>
                  )}
                </AccordionTab>

                <AccordionTab header="✏️ Editing Customers">
                  <h4>Inline Editing (Quick Edit)</h4>
                  <ol>
                    <li>Click directly on any cell in the table</li>
                    <li>The cell becomes editable with appropriate input:
                      <ul>
                        <li>Text fields for names, emails, company</li>
                        <li>Dropdowns for country and language</li>
                        <li>Checkboxes for PKW/NKW data</li>
                      </ul>
                    </li>
                    <li>Make your changes</li>
                    <li>Click outside the cell or press Enter to save</li>
                    <li>Press Escape to cancel changes</li>
                  </ol>

                  <h4>Form Editing (Full Edit)</h4>
                  <ol>
                    <li>Click the <strong>✏️ Edit</strong> button in the Actions column</li>
                    <li>The customer form opens with all current data</li>
                    <li>Make your changes</li>
                    <li>Click <strong>Save</strong> to update</li>
                  </ol>

                  <div className="warning-box">
                    <strong>⚠️ Validation:</strong> Invalid data is highlighted with a red border and ❌ marker.
                    Fix validation errors before saving.
                  </div>
                </AccordionTab>

                <AccordionTab header="🗑️ Deleting Customers">
                  <h4>Delete Single Customer</h4>
                  <ol>
                    <li>Click the <strong>🗑️ Delete</strong> button in the Actions column</li>
                    <li>Confirm the deletion in the dialog</li>
                  </ol>

                  <h4>Delete Multiple Customers</h4>
                  <ol>
                    <li>Check the boxes next to customers you want to delete</li>
                    <li>Click the <strong>Delete</strong> button in the toolbar</li>
                    <li>Confirm the deletion in the dialog</li>
                  </ol>

                  {!isAdmin && (
                    <div className="info-box">
                      <strong>Note:</strong> You can only delete customers from your organization.
                    </div>
                  )}
                </AccordionTab>

                <AccordionTab header="🔍 Searching and Filtering">
                  <h4>Global Search</h4>
                  <p>Use the search box in the toolbar to search across all visible columns.</p>

                  <h4>Organization Filter</h4>
                  <ol>
                    <li>Click the <strong>🏢 Organization</strong> filter button</li>
                    <li>Select one or more organizations</li>
                    <li>Click <strong>Apply</strong> to filter the table</li>
                  </ol>

                  <h4>Country Filter</h4>
                  <ol>
                    <li>Click the <strong>🌍 Country</strong> filter button</li>
                    <li>Select one or more countries</li>
                    <li>Click <strong>Apply</strong> to filter the table</li>
                  </ol>

                  {!isAdmin && (
                    <div className="info-box">
                      <strong>Note:</strong> You can only see customers from your organization.
                    </div>
                  )}
                </AccordionTab>

                <AccordionTab header="✅ Selection and Bulk Actions">
                  <h4>Selecting Customers</h4>
                  <ul>
                    <li><strong>Select All (This Page)</strong> - Check the header checkbox to select only the rows visible on the current page</li>
                    <li><strong>Select All (Every Page)</strong> - After checking the header checkbox, click "Select all X customers across all pages" in the blue banner to select the entire list, not just the current page</li>
                    <li><strong>Individual Selection</strong> - Check individual row checkboxes</li>
                  </ul>

                  <h4>Bulk Actions</h4>
                  <p>Once customers are selected, you can:</p>
                  <ul>
                    <li><strong>Export Selected</strong> - Export only selected customers to CSV</li>
                    <li><strong>Delete Selected</strong> - Delete all selected customers at once</li>
                  </ul>

                  <div className="info-box">
                    <strong>Tip:</strong> Use filters to narrow down customers before selecting all.
                  </div>
                </AccordionTab>
              </Accordion>
            </div>
          </TabPanel>

          <TabPanel header="Import/Export">
            <div className="help-section">
              <Accordion multiple>
                <AccordionTab header="📥 Importing from CSV">
                  <h4>CSV Import Process</h4>
                  <ol>
                    <li>Click the <strong>Import</strong> button in the toolbar</li>
                    <li>Click <strong>Choose File</strong> and select your CSV file</li>
                    <li>The system validates the CSV format</li>
                    <li>Valid records are imported automatically</li>
                    <li>Invalid records are skipped with error messages</li>
                  </ol>

                  <h4>CSV Format Requirements</h4>
                  <p>Your CSV file must have these columns in this exact order:</p>
                  <ol>
                    <li>customerNr</li>
                    <li>primaryEmail</li>
                    <li>secondaryEmail</li>
                    <li>firstName</li>
                    <li>surName</li>
                    <li>company</li>
                    <li>company2</li>
                    <li>country</li>
                    <li>language</li>
                    <li>telephone</li>
                    <li>pkwData (0 or 1)</li>
                    <li>nkwData (0 or 1)</li>
                    <li>organization</li>
                  </ol>

                  <h4>Example CSV</h4>
                  <pre className="code-block">
customerNr;primaryEmail;secondaryEmail;firstName;surName;company;company2;country;language;telephone;pkwData;nkwData;organization
12345;john@example.com;john.alt@example.com;John;Doe;Acme Corp;;DE;de;+49123456789;1;0;TecAlliance
                  </pre>

                  <div className="warning-box">
                    <strong>⚠️ Important:</strong>
                    <ul>
                      <li>Use semicolon (;) as separator</li>
                      <li>First row must be headers</li>
                      <li>All rows must have exactly 13 fields</li>
                      <li>pkwData and nkwData must be 0 or 1</li>
                    </ul>
                  </div>

                  {!isAdmin && (
                    <div className="info-box">
                      <strong>Note:</strong> Imported customers are automatically assigned to your organization,
                      regardless of the organization field in the CSV.
                    </div>
                  )}
                </AccordionTab>

                <AccordionTab header="📤 Exporting to CSV">
                  <h4>Export Process</h4>
                  <ol>
                    <li>Select the customers you want to export (or select all)</li>
                    <li>Click the <strong>Export</strong> button in the toolbar</li>
                    <li>The system validates selected customers</li>
                    <li>Invalid customers are automatically excluded</li>
                    <li>A CSV file is downloaded to your computer</li>
                  </ol>

                  <h4>Export Options</h4>
                  <ul>
                    <li><strong>Export Selected</strong> - Only exports checked customers</li>
                    <li><strong>Export Filtered</strong> - First apply filters, select all, then export</li>
                  </ul>

                  <div className="info-box">
                    <strong>Tip:</strong> Use filters and selection to export specific subsets of customers.
                  </div>

                  <div className="warning-box">
                    <strong>⚠️ Validation:</strong> Customers with validation errors (marked with ❌) are
                    automatically excluded from export. Fix validation errors before exporting.
                  </div>
                </AccordionTab>
              </Accordion>
            </div>
          </TabPanel>

          <TabPanel header="Validation">
            <div className="help-section">
              <h3>📋 Data Validation</h3>
              <p>
                The system automatically validates customer data to ensure data quality.
                Invalid data is highlighted with visual indicators.
              </p>

              <h4>Validation Indicators</h4>
              <ul>
                <li><strong>❌ Red Marker</strong> - Row has validation errors</li>
                <li><strong>Red Border</strong> - Specific field has validation error</li>
                <li><strong>✓ No Marker</strong> - All data is valid</li>
              </ul>

              <h4>Validation Rules</h4>
              <Accordion>
                <AccordionTab header="Customer Number">
                  <ul>
                    <li>Required field</li>
                    <li>Must be unique</li>
                    <li>Cannot be empty</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Primary Email">
                  <ul>
                    <li>Required field</li>
                    <li>Must be valid email format (user@domain.com)</li>
                    <li>Cannot contain spaces</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Secondary Email">
                  <ul>
                    <li>Optional field</li>
                    <li>If provided, must be valid email format</li>
                    <li>Can be left empty</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Surname">
                  <ul>
                    <li>Required field</li>
                    <li>Cannot be empty</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Company">
                  <ul>
                    <li>Required field</li>
                    <li>Cannot be empty</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Country">
                  <ul>
                    <li>Required field</li>
                    <li>Must be valid country code (DE, FR, IT, etc.)</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Language">
                  <ul>
                    <li>Required field</li>
                    <li>Must be valid language code (de, en, fr, etc.)</li>
                  </ul>
                </AccordionTab>
              </Accordion>

              <h4>Fixing Validation Errors</h4>
              <ol>
                <li>Look for rows with ❌ marker</li>
                <li>Click on cells with red borders</li>
                <li>Correct the invalid data</li>
                <li>Save the changes</li>
                <li>The ❌ marker disappears when all errors are fixed</li>
              </ol>

              <div className="warning-box">
                <strong>⚠️ Important:</strong> Customers with validation errors cannot be exported to CSV.
                Fix all errors before exporting.
              </div>
            </div>
          </TabPanel>

          {isAdmin && (
            <TabPanel header="Admin Features">
              <div className="help-section">
                <h3>👑 Administrator Features</h3>
                <p>
                  As an administrator, you have additional capabilities beyond regular users.
                </p>

                <Accordion multiple>
                  <AccordionTab header="🏢 Organization Management">
                    <h4>Viewing All Organizations</h4>
                    <p>
                      You can see customers from all organizations in the system.
                      Use the Organization filter to view specific organizations.
                    </p>

                    <h4>Assigning Organizations</h4>
                    <p>
                      When creating or editing customers, you can assign them to any organization:
                    </p>
                    <ol>
                      <li>Open the customer form (create or edit)</li>
                      <li>Select the organization from the dropdown</li>
                      <li>Save the customer</li>
                    </ol>

                    <div className="info-box">
                      <strong>Note:</strong> Regular users cannot change organization assignments.
                    </div>
                  </AccordionTab>

                  <AccordionTab header="🗑️ Cross-Organization Deletion">
                    <h4>Deleting Any Customer</h4>
                    <p>
                      As an admin, you can delete customers from any organization:
                    </p>
                    <ul>
                      <li>Single deletion works across all organizations</li>
                      <li>Bulk deletion works across all organizations</li>
                      <li>No organization restrictions apply</li>
                    </ul>

                    <div className="warning-box">
                      <strong>⚠️ Caution:</strong> Deletions are permanent. Always verify before deleting.
                    </div>
                  </AccordionTab>

                  <AccordionTab header="📊 Data Management">
                    <h4>Bulk Operations</h4>
                    <p>Admins can perform bulk operations across all organizations:</p>
                    <ul>
                      <li>Export customers from multiple organizations</li>
                      <li>Import customers to any organization</li>
                      <li>Delete customers across organizations</li>
                    </ul>

                    <h4>Data Quality</h4>
                    <p>Monitor and maintain data quality:</p>
                    <ul>
                      <li>Review validation errors across all organizations</li>
                      <li>Fix data inconsistencies</li>
                      <li>Ensure compliance with data standards</li>
                    </ul>
                  </AccordionTab>

                  <AccordionTab header="📜 Change History">
                    <h4>Viewing Change History</h4>
                    <p>
                      The Change History tracks all modifications to customer data for audit purposes.
                    </p>
                    <ol>
                      <li>Click the <strong>📜 History</strong> button in the toolbar</li>
                      <li>A sidebar opens showing all recent changes</li>
                    </ol>

                    <h4>Change History Details</h4>
                    <p>Each entry shows:</p>
                    <ul>
                      <li><strong>Time</strong> — When the change occurred</li>
                      <li><strong>Action</strong> — CREATE, UPDATE, or DELETE</li>
                      <li><strong>User</strong> — Who made the change (name or email)</li>
                      <li><strong>Customer</strong> — Which customer was affected</li>
                    </ul>

                    <h4>Viewing Details</h4>
                    <ol>
                      <li>Click the <strong>👁️ View</strong> button on any entry</li>
                      <li>A dialog shows the before/after values for each field</li>
                      <li>Changed fields are highlighted in yellow</li>
                      <li>Previous values appear in red (strikethrough)</li>
                      <li>New values appear in green (bold)</li>
                    </ol>

                    <div className="info-box">
                      <strong>Note:</strong> Change history entries are retained for 365 days.
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            </TabPanel>
          )}

          {isUserAdmin && (
            <TabPanel header="User Management">
              <div className="help-section">
                <h3>👥 User Management</h3>
                <p>
                  As a User Administrator, you can manage Cognito user accounts directly from the application.
                </p>

                <Accordion multiple>
                  <AccordionTab header="📋 Viewing Users">
                    <ol>
                      <li>Click <strong>User Management</strong> in the top navigation bar</li>
                      <li>The user list shows all Cognito users with their status</li>
                      <li>Use the search bar to filter by name, email, or username</li>
                    </ol>

                    <h4>User Information</h4>
                    <ul>
                      <li><strong>Username</strong> — Cognito username</li>
                      <li><strong>Email</strong> — User's email address</li>
                      <li><strong>Name</strong> — Display name</li>
                      <li><strong>Enabled</strong> — Whether the account is active</li>
                      <li><strong>Status</strong> — Account status (CONFIRMED, FORCE_CHANGE_PASSWORD, etc.)</li>
                      <li><strong>Created</strong> — When the account was created</li>
                    </ul>
                  </AccordionTab>

                  <AccordionTab header="➕ Creating Users">
                    <ol>
                      <li>Click the <strong>Create User</strong> button</li>
                      <li>Fill in the form:
                        <ul>
                          <li><strong>Email</strong> — User's email (required, used for login)</li>
                          <li><strong>Name</strong> — Display name (optional)</li>
                          <li><strong>Groups</strong> — Select one or more roles</li>
                        </ul>
                      </li>
                      <li>Click <strong>Save</strong></li>
                    </ol>

                    <div className="info-box">
                      <strong>Note:</strong> The user receives a temporary password via email and must change it on first login.
                    </div>

                    <h4>Available Groups</h4>
                    <ul>
                      <li><strong>GS-ADMIN</strong> — Full access to all organizations</li>
                      <li><strong>GS-USER</strong> — Access to own organization only</li>
                      <li><strong>GS-ORGANIZATION</strong> — Organization-level access</li>
                      <li><strong>GS-USER-ADMIN</strong> — Can manage user accounts</li>
                      <li><strong>READ-ONLY-USER</strong> — Read-only access</li>
                    </ul>
                  </AccordionTab>

                  <AccordionTab header="✏️ Editing Users">
                    <ol>
                      <li>Click on a user row in the list</li>
                      <li>The detail form opens with current user data</li>
                      <li>Update email, name, or group assignments</li>
                      <li>Click <strong>Save</strong> to apply changes</li>
                    </ol>

                    <div className="info-box">
                      <strong>Tip:</strong> Group changes take effect on the user's next login.
                    </div>
                  </AccordionTab>

                  <AccordionTab header="🗑️ Deleting Users">
                    <ol>
                      <li>Click on a user row to open the detail form</li>
                      <li>Click the <strong>Delete</strong> button</li>
                      <li>Confirm the deletion in the dialog</li>
                    </ol>

                    <div className="warning-box">
                      <strong>⚠️ Caution:</strong> User deletion is permanent and cannot be undone.
                      The user will lose all access immediately.
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            </TabPanel>
          )}

          <TabPanel header="Tips & Tricks">
            <div className="help-section">
              <h3>💡 Tips & Tricks</h3>

              <Accordion multiple>
                <AccordionTab header="⚡ Keyboard Shortcuts">
                  <ul>
                    <li><strong>Enter</strong> - Save inline edit</li>
                    <li><strong>Escape</strong> - Cancel inline edit</li>
                    <li><strong>Tab</strong> - Move to next cell (in edit mode)</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="🎯 Efficient Workflows">
                  <h4>Quick Editing</h4>
                  <p>Use inline editing for quick changes to single fields without opening the full form.</p>

                  <h4>Bulk Updates</h4>
                  <ol>
                    <li>Export customers to CSV</li>
                    <li>Edit in Excel/spreadsheet</li>
                    <li>Delete old customers</li>
                    <li>Import updated CSV</li>
                  </ol>

                  <h4>Filtering Before Export</h4>
                  <ol>
                    <li>Apply organization and country filters</li>
                    <li>Select all filtered customers</li>
                    <li>Export to get specific subset</li>
                  </ol>
                </AccordionTab>

                <AccordionTab header="🔍 Finding Customers">
                  <h4>Search Strategies</h4>
                  <ul>
                    <li>Use global search for quick lookups</li>
                    <li>Combine filters for precise results</li>
                    <li>Sort columns by clicking headers</li>
                  </ul>

                  <h4>Common Searches</h4>
                  <ul>
                    <li>Search by email to find specific customer</li>
                    <li>Search by company name to find all employees</li>
                    <li>Filter by country to see regional customers</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="✅ Best Practices">
                  <ul>
                    <li>Always validate data before exporting</li>
                    <li>Use descriptive customer numbers</li>
                    <li>Keep email addresses up to date</li>
                    <li>Regularly review and clean up invalid data</li>
                    <li>Use filters to work with manageable subsets</li>
                    <li>Double-check before bulk deletions</li>
                  </ul>
                </AccordionTab>
              </Accordion>
            </div>
          </TabPanel>

          <TabPanel header="Troubleshooting">
            <div className="help-section">
              <h3>🔧 Troubleshooting</h3>

              <Accordion multiple>
                <AccordionTab header="❌ Validation Errors">
                  <h4>Problem: Red markers on rows</h4>
                  <p><strong>Solution:</strong></p>
                  <ol>
                    <li>Click on cells with red borders</li>
                    <li>Check the validation rules for that field</li>
                    <li>Correct the data according to the rules</li>
                    <li>Save the changes</li>
                  </ol>
                </AccordionTab>

                <AccordionTab header="📥 Import Failures">
                  <h4>Problem: CSV import fails</h4>
                  <p><strong>Common Causes:</strong></p>
                  <ul>
                    <li>Wrong separator (must be semicolon ;)</li>
                    <li>Missing or extra columns</li>
                    <li>Invalid data format</li>
                    <li>Missing required fields</li>
                  </ul>
                  <p><strong>Solution:</strong></p>
                  <ol>
                    <li>Check CSV has exactly 13 columns</li>
                    <li>Verify semicolon separator</li>
                    <li>Ensure first row is headers</li>
                    <li>Validate data format matches requirements</li>
                  </ol>
                </AccordionTab>

                <AccordionTab header="📤 Export Issues">
                  <h4>Problem: Export button disabled</h4>
                  <p><strong>Solution:</strong> Select at least one customer before exporting.</p>

                  <h4>Problem: Some customers not exported</h4>
                  <p><strong>Solution:</strong> Customers with validation errors are automatically excluded.
                  Fix validation errors first.</p>
                </AccordionTab>

                <AccordionTab header="🔍 Can't Find Customers">
                  <h4>Problem: Expected customers not visible</h4>
                  <p><strong>Solution:</strong></p>
                  <ul>
                    <li>Check if filters are applied</li>
                    <li>Clear search box</li>
                    <li>Verify you have access to that organization (regular users)</li>
                    <li>Refresh the page</li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="💾 Changes Not Saving">
                  <h4>Problem: Inline edits not persisting</h4>
                  <p><strong>Solution:</strong></p>
                  <ol>
                    <li>Ensure you pressed Enter or clicked outside the cell</li>
                    <li>Check for validation errors (red border)</li>
                    <li>Verify you have permission to edit that customer</li>
                    <li>Check your internet connection</li>
                  </ol>
                </AccordionTab>
              </Accordion>

              <div className="info-box">
                <strong>Still having issues?</strong> Contact your system administrator for assistance.
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </Sidebar>
  );
};
